import { useState, useRef, useEffect } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { Link, router, usePage } from "@inertiajs/react"
import { Inbox, Sparkles, Users } from "lucide-react"
import { toast } from "@/components/ui/sonner"
import { Layout } from "@/components/layouts/Layout"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"

export default function WorkspacesIndex({ workspaces }) {
  const { current_workspace } = usePage().props
  const [localCurrentId, setLocalCurrentId] = useState(current_workspace?.id)
  const [scrolled, setScrolled] = useState(false)
  const [hasScrolled, setHasScrolled] = useState(false)
  const scrollContainerRef = useRef(null)
  const nameRef = useRef(null)

  useEffect(() => {
    const root = scrollContainerRef.current
    const target = nameRef.current
    if (!root || !target) return

    const observer = new IntersectionObserver(
      ([entry]) => setScrolled(!entry.isIntersecting),
      { root, threshold: 0 }
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [])

  function handleScrollToTop() {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  function handleScroll(e) {
    setHasScrolled(e.currentTarget.scrollTop > 0)
  }

  function handleSwitch(slug, wsId, wsName) {
    setLocalCurrentId(wsId)
    toast.success(`Switched to ${wsName}`)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(`/workspaces/${slug}/switch`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken }
    })
  }

  return (
    <Layout insetClassName="overflow-hidden">

      <header className={`flex items-center justify-between gap-4 px-4 h-16 shrink-0 transition-[width,height,border-color] ease-out group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 border-b ${hasScrolled ? "border-border" : "border-transparent"}`}>

        <div className="flex items-center gap-1">
          <AnimatePresence>
            {scrolled && (
              <motion.button
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                onClick={handleScrollToTop}
                className="flex items-center gap-1.5 min-w-0 rounded-md px-2 h-7 hover:bg-accent transition-colors cursor-pointer"
              >
                <span className="text-sm font-medium truncate">Workspaces</span>
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {scrolled && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
            >
              <Button size="sm" asChild>
                <Link href="/workspaces/new">New workspace</Link>
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

      </header>

      <ScrollArea className="flex-1 min-h-0" viewportRef={scrollContainerRef} onScroll={handleScroll}>
        <div className="flex flex-col gap-8 px-6 pb-6">

          <div ref={nameRef}>
            <h1 className="text-3xl font-medium">Workspaces</h1>
          </div>

          <div className="max-w-2xl rounded-lg border divide-y overflow-hidden">
            {workspaces?.map((ws) => {
              const isCurrent = ws.id === localCurrentId
              return (
                <div
                  key={ws.id}
                  className="group flex items-center justify-between gap-4 p-4 hover:bg-accent cursor-pointer transition-colors"
                  onClick={() => router.visit(`/workspaces/${ws.slug}`)}
                >
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-base font-medium truncate">{ws.name}</span>
                      <AnimatePresence>
                        {isCurrent && (
                          <motion.div
                            key="current-badge"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="sky">
                                  Current
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>The workspace you&apos;re currently working in</TooltipContent>
                            </Tooltip>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <Inbox className="h-3.5 w-3.5 shrink-0" />
                        inbox@{ws.slug}.decodekit.ai
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5 shrink-0" />
                        {ws.agents_count} {ws.agents_count === 1 ? "agent" : "agents"}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5 shrink-0" />
                        {ws.members_count} {ws.members_count === 1 ? "member" : "members"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {!isCurrent && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleSwitch(ws.slug, ws.id, ws.name) }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Switch to
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => e.stopPropagation()}
                      asChild
                    >
                      <Link href={`/workspaces/${ws.slug}`}>View</Link>
                    </Button>
                  </div>
                </div>
              )
            })}

            {(!workspaces || workspaces.length === 0) && (
              <p className="text-sm text-muted-foreground px-4 py-4">No workspaces found.</p>
            )}
          </div>

          <div className="max-w-2xl">
            <Button size="sm" asChild>
              <Link href="/workspaces/new">New workspace</Link>
            </Button>
          </div>

        </div>
      </ScrollArea>

    </Layout>
  )
}
